import type { OnLoadConfig, ProactiveTrigger } from './../../api/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { captureMessage, getCurrentScope, setTag } from '@sentry/react';
import tinycolor from 'tinycolor2';
import configInitialState from 'reducers/config/initialState';
//Redux
import {
  configWidget,
  resetPreviewWidgetConfig,
  sendTrackingEvent,
  setInitialIntentId,
  setInitialQuery,
  setIntentTitle,
  setIsBuilderPreview,
  setIsModelTraining,
  setIsSolveConfigPreview,
  setLastProactiveTriggerId,
  setLogRocketInitialized,
  setParentUrl,
  setWorkflowVersion,
} from 'actions/workflows/workflows';
import { setAPIKey, setOrigin, addRawParams } from 'actions/config';
import {
  selectIsPrivacyConsented,
  selectLegibleElementColor,
  selectLegibleTextColor,
  selectThemeColor,
} from 'reducers/config';
import {
  privacyConsented,
  selectAreLoadingDotsVisible,
  selectConversationId,
  selectDidUserRestartConversation,
  selectInitialIntentId,
  selectIsScrollAtTheBottom,
  selectIsBuilderPreview,
  selectIsConversationStarted,
  selectIsSolveConfigPreview,
  selectIsWidgetEnabled,
  selectIsWorkflowTagSelectionActive,
  selectSessionId,
  selectShouldInitLogRocket,
  selectVisibleWidgetComponents,
  selectWorkflowsConfig,
  setActionToExecAfterPrivacyConsented,
  selectInitialQuery,
  selectIsProactiveChatVisible,
  showProactivePrompt,
  selectFeatureFlags,
  selectProactiveTriggers,
} from 'reducers/workflows';
import {
  initIntentConversation,
  postForethoughtWidgetLoaded,
  updateIntentConversation,
} from 'actions/workflows-v2/workflowsV2';

import { setIsMobile, openWidget } from 'actions/main';
import { selectIsWidgetVisible } from 'reducers/main';

//Types
import type { ContextVariables } from 'api/types';

//Utils
import { getLogRocketKey } from './utils';
import {
  isEventTrusted,
  postMessage,
  setTargetOrigin,
} from '../../utils/postMessage';
import { useTheme } from '@mui/material/styles';
import { setRawParams, setWidgetInitMethod } from 'actions/config/config';
import getParentOrigin from 'utils/getParentOrigin';
import lazyLogRocket from 'lazy-logrocket';
import ApiService from 'services/ApiService';
import { scrollToTheBottom } from 'utils/helpers';
import {
  getDisabledUntil,
  isForethoughtDisabledByLocalStorage,
} from 'utils/disableForethoughtWidget';
import {
  selectAreAgentTypingDotsVisible,
  selectChatId,
} from 'reducers/live-chat';
import type {
  ForethoughtEvent,
  ForethoughtWidgetConversationIdGeneratedEvent,
  ForethoughtWidgetScreenSizeEvent,
} from 'embed/embed';
import type { ForethoughtWidgetInitEvent } from 'embed/embed';
import { initializeKustomerSdkOnWidgetLoad } from './hooks/useHandleKustomerChatInitialization';
import { useCallbackOnce } from './hooks/useCallbackOnce';
import { getStore } from 'storeRef';

const useInitSolveCallback = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    // typeof MessageEvent.data is any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (data: Record<string, any>) => {
      const apiKey = data.API_KEY;
      const isBuilderPreview = data.isBuilderPreview;
      const isSolveConfigPreview = data.isSolveConfigPreview;
      const workflowVersion = data.workflowVersion;
      const origin = data.origin;
      const href = data.href;
      const resetWidget = data.resetWidget;
      const widgetInitMethod = data.widgetInitMethod;
      const initialIntentId = data.rawParams?.['initial-intent-id'];
      const initialQuery = data.rawParams?.['initial-query'];
      const isModelTraining =
        data.rawParams?.['data-is-model-training'] === 'true';

      const intentId: string | undefined = data.rawParams?.['data-intent-id'];
      const intentTitle: string | undefined =
        data.rawParams?.['data-intent-title'];
      const isTemplate = intentId?.startsWith('template');

      if (isModelTraining) {
        dispatch(setIsModelTraining(isModelTraining));
      }

      if (Object.prototype.hasOwnProperty.call(data, 'API_KEY')) {
        if (isBuilderPreview) dispatch(setIsBuilderPreview(isBuilderPreview));
        if (isSolveConfigPreview) {
          dispatch(setIsSolveConfigPreview(isSolveConfigPreview));
        }
        if (href) {
          dispatch(setParentUrl(href));
        }
        if (workflowVersion === null || workflowVersion)
          dispatch(setWorkflowVersion(workflowVersion));
        if (origin) {
          setTargetOrigin(data.origin);
          dispatch(setOrigin(data.origin));
          setTag('solve-origin', data.origin);
        }
        if (apiKey) {
          dispatch(setAPIKey(apiKey));
          if (isBuilderPreview) {
            dispatch(setRawParams(data.rawParams));
          } else dispatch(addRawParams(data.rawParams));
        }
        if (initialIntentId) {
          dispatch(setInitialIntentId(initialIntentId));
        }

        if (initialQuery) {
          dispatch(setInitialQuery(initialQuery));
        }
        // Resets data when exchanging tabs in workflow builder (workflows vs intent detection)
        if (resetWidget) {
          dispatch(resetPreviewWidgetConfig());
          await dispatch(configWidget());
        }

        if (!!intentTitle) {
          dispatch(setIntentTitle(intentTitle));
        }

        if (!!intentId && !isTemplate) {
          dispatch(
            initIntentConversation({
              intent: {
                intent_id: intentId,
              },
            }),
          );
        }

        if (widgetInitMethod) {
          dispatch(setWidgetInitMethod(widgetInitMethod));
        }
      } else if (Object.prototype.hasOwnProperty.call(data, 'isMobile')) {
        dispatch(setIsMobile(data.isMobile));
      }

      if (Object.prototype.hasOwnProperty.call(data, 'origin') && data.origin) {
        setTargetOrigin(data.origin);
        dispatch(setOrigin(data.origin));
        setTag('solve-origin', data.origin);
      }

      if (apiKey) {
        await dispatch(configWidget());

        // If widget is disabled via local storage, send tracking event
        const disabledUntil = getDisabledUntil(apiKey);
        if (disabledUntil && isForethoughtDisabledByLocalStorage(apiKey)) {
          dispatch(
            sendTrackingEvent({
              current_timestamp: Date.now(),
              disabled_until_timestamp: disabledUntil.timestamp,
              event_type: 'widget-disabled-by-local-storage',
              original_conversation_id: disabledUntil.conversationId,
            }),
          );

          postMessage({ event: 'forethoughtWidgetDisabled' });
        }

        if (isTemplate && isSolveConfigPreview && intentId) {
          dispatch(
            initIntentConversation({
              intent: {
                intent_id: intentId,
              },
            }),
          );
        }
      }
    },
    [dispatch],
  );
};

export const useInitSolve = (): void => {
  const dispatch = useAppDispatch();
  const isWidgetEnabled = useAppSelector(selectIsWidgetEnabled);
  const initSolve = useInitSolveCallback();
  const postWidgetStartedInitializingOnce = useCallbackOnce();
  const postWidgetLoadedOnce = useCallbackOnce();

  useEffect(() => {
    postWidgetStartedInitializingOnce(() => {
      postMessage({ event: 'forethoughtWidgetStartedInitializing' });
    });

    const handleMessage = async (
      e:
        | MessageEvent
        | ForethoughtWidgetInitEvent
        | ForethoughtWidgetScreenSizeEvent,
    ): Promise<void> => {
      if (!isEventTrusted(e)) {
        return;
      }

      if (e.data.event === 'forethoughtWidgetInitEvent') {
        initSolve(e.data);

        return;
      }

      if (e.data.event === 'forethoughtWidgetScreenSizeEvent') {
        dispatch(setIsMobile(e.data.isMobile));

        return;
      }
    };

    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [dispatch, initSolve, postWidgetStartedInitializingOnce]);

  useEffect(() => {
    // post to parent window
    if (isWidgetEnabled) {
      postWidgetLoadedOnce(() => {
        dispatch(postForethoughtWidgetLoaded());
      });
    }
  }, [dispatch, isWidgetEnabled, postWidgetLoadedOnce]);
};

export const useInitSolveFromURL = (): void => {
  const dispatch = useAppDispatch();
  const initSolve = useInitSolveCallback();
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();
  const initOnce = useCallbackOnce();

  useEffect(() => {
    const initFromUrl = async () => {
      const url = new URL(window?.location?.href);
      const params: Record<string, unknown> = url.searchParams?.keys()
        ? Object.fromEntries(url.searchParams)
        : {};
      const rawParams = url.searchParams?.keys()
        ? Object.fromEntries(url.searchParams)
        : {};
      const apiKey = url.searchParams?.get('data-api-key');
      const isPreview =
        url.searchParams?.get('preview') === 'true' ||
        url.searchParams?.get('data-interactive-email-preview') === 'true';

      const dataIntentId = url.searchParams?.get('data-intent-id');
      const initialIntentId = url.searchParams?.get('initial-intent-id');
      // data-intent-id is not supported when initializing from URL
      delete params['data-intent-id'];
      const intentId = initialIntentId ? initialIntentId : dataIntentId;
      if (intentId) {
        params['initial-intent-id'] = intentId;
      }

      if (apiKey) {
        params['API_KEY'] = apiKey;
      }

      if (isPreview) {
        params.isSolveConfigPreview = isPreview;
        params.origin = getParentOrigin();
      }

      params.rawParams = rawParams;

      await initSolve(params);

      const fullScreen = url.searchParams?.get('full-screen');

      if (fullScreen) {
        dispatch(openWidget());
        handleWorkflowTriggerOnClick({ shouldWaitForWorkflowsConfig: false });
        dispatch(
          sendTrackingEvent({
            event_type: 'widget-opened',
            mechanism: 'full-screen-true',
          }),
        );
      }
    };

    initOnce(() => {
      initFromUrl();
    });
  }, [dispatch, handleWorkflowTriggerOnClick, initOnce, initSolve]);
};

export const useSetupSentry = (): void => {
  useEffect(() => {
    lazyLogRocket.getSessionURL(sessionURL => {
      const scope = getCurrentScope();
      scope.setExtra('sessionURL', sessionURL);
    });
  }, []);
};

export const useSetCustomConfigs = (): void => {
  const theme = useTheme();
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);
  const themeColor = useAppSelector(selectThemeColor);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);

  useEffect(() => {
    let hoveredTheme;
    const documentElement =
      document.documentElement ?? document.querySelector('html');
    if (workflowsConfig && documentElement) {
      documentElement.style.setProperty('--theme', themeColor);
      documentElement.style.setProperty(
        '--legible-text-color',
        legibleTextColor,
      );
      documentElement.style.setProperty(
        '--legible-element-color',
        legibleElementColor,
      );
      if (themeColor !== theme.palette.colors.black)
        hoveredTheme = tinycolor(themeColor).darken(6).toString();
      else hoveredTheme = tinycolor(themeColor).lighten(25).toString();

      documentElement.style.setProperty('--hovered-theme', hoveredTheme);
    }
  }, [
    workflowsConfig,
    themeColor,
    legibleTextColor,
    legibleElementColor,
    theme.palette.colors.black,
  ]);
};

export const useLogRocket = (): void => {
  const dispatch = useAppDispatch();
  const isWidgetVisible = useAppSelector(selectIsWidgetVisible);
  const shouldInitLogRocket = useAppSelector(selectShouldInitLogRocket);
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);
  const isBuilderPreview = useAppSelector(selectIsBuilderPreview);
  const isSolveConfigPreview = useAppSelector(selectIsSolveConfigPreview);
  const persistedConversationId = useAppSelector(selectConversationId);
  const sessionId = useAppSelector(selectSessionId);

  useEffect(() => {
    if (isWidgetVisible && !shouldInitLogRocket)
      dispatch(setLogRocketInitialized(true));
  }, [isWidgetVisible, shouldInitLogRocket, dispatch]);

  useEffect(() => {
    if (workflowsConfig?.logrocket_enabled && shouldInitLogRocket) {
      const key = getLogRocketKey();

      if (key) {
        lazyLogRocket.init(key, {
          console: {
            shouldAggregateConsoleErrors: true,
          },
          network: {
            requestSanitizer: request => {
              request.headers['Authorization'] = '';
              return request;
            },
          },
          release: process.env.REACT_APP_TAG || 'local',
        });

        lazyLogRocket.identify({
          channel:
            isBuilderPreview || isSolveConfigPreview ? 'preview' : 'live',
          conversationId: persistedConversationId || '',
          origin: getParentOrigin(),
          product: 'flamethrower',
          ...(typeof sessionId === 'number' ? { session_id: sessionId } : {}),
        });
      }
    }
  }, [
    isBuilderPreview,
    isSolveConfigPreview,
    persistedConversationId,
    sessionId,
    shouldInitLogRocket,
    workflowsConfig?.logrocket_enabled,
  ]);
};

export const useHandleScrollBehavior = (): void => {
  const isScrollAtTheBottom = useAppSelector(selectIsScrollAtTheBottom);
  const visibleWidgetComponents = useAppSelector(selectVisibleWidgetComponents);
  const areLoadingDotsVisible = useAppSelector(selectAreLoadingDotsVisible);
  const areAgentTypingDotsVisible = useAppSelector(
    selectAreAgentTypingDotsVisible,
  );

  useEffect(() => {
    if (isScrollAtTheBottom) {
      const firstInput: HTMLElement | null = document.querySelector(
        '.js-form-step textarea, .js-form-step input[type=text], .js-form-step input[type=email], .js-form-step input[type=tel], .js-form-step input[type=date]',
      );
      const firstLabel = document.querySelector<HTMLLabelElement>(
        '.js-form-step label',
      );

      if (firstLabel) {
        firstLabel.scrollIntoView({ behavior: 'smooth' });
      } else if (firstInput) {
        // `firstLabel` should always exist, but still use `firstInput` as a
        // fallback:
        firstInput.scrollIntoView({ behavior: 'smooth' });
      } else {
        scrollToTheBottom('smooth');
      }

      firstInput?.focus({
        // `preventScroll: true` is needed to avoid glitching.
        // We already scrolled using `scrollIntoView()` a few lines above.
        preventScroll: true,
      });
    }

    /**
     * We want to trigger this effect when loading dots appear, and when a new
     * message gets rendered, so `visibleWidgetComponents.length`,
     * `areLoadingDotsVisible`, and 'areAgentTypingDotsVisible' have to be in dependencies,
     * even though they are not actually used.
     */
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
    `isScrollAtTheBottom` DOES NOT need to be in dependencies, to prevent
    autofocusing the first form input when users scrolls to the bottom. */
  }, [
    visibleWidgetComponents.length,
    areLoadingDotsVisible,
    areAgentTypingDotsVisible,
  ]);
};

export const useUpdateConversation = () => {
  const dispatch = useAppDispatch();

  const updateConversationHandler = useCallback(
    async (
      contextVariables: ContextVariables,
      {
        isContinueAutoflowIntent = false,
      }: { isContinueAutoflowIntent?: boolean } = {},
    ) => {
      await dispatch(
        updateIntentConversation({
          contextVariables,
          isContinueAutoflowIntent,
        }),
      );
    },
    [dispatch],
  );

  return updateConversationHandler;
};

export const useSetTabIdSentryTag = () => {
  useEffect(() => {
    setTag('tab-id', configInitialState.lastActiveTabId);
  }, []);
};

async function waitForWorkflowsConfig() {
  const store = getStore();
  while (!selectWorkflowsConfig(store.getState())) {
    await new Promise<void>(resolve => {
      setTimeout(resolve, 100);
    });
  }
}

export const useHandleWorkflowTriggerOnClick = ({
  triggerFromPrivacyConsent = false,
}: {
  triggerFromPrivacyConsent?: boolean;
} = {}) => {
  const dispatch = useAppDispatch();
  const triggeredRef = useRef<boolean>(false);

  /**
   * Persistence enabled
   * - Initial intent id starts on initial conversation onClick
   * - In ongoing conversation do not trigger initial-intent-id or initial-query (case doesn't exist for non-persistence)
   * Not enabled
   * - Initial intent id starts on initial conversation onClick
   * - After browser refresh starts a new initial conversation onClick
   */
  const onClick = useCallback(
    async ({
      initialQueryParam,
      shouldWaitForWorkflowsConfig = true,
    }: {
      initialQueryParam?: string;
      shouldWaitForWorkflowsConfig?: boolean;
    } = {}) => {
      if (shouldWaitForWorkflowsConfig) {
        await waitForWorkflowsConfig();
      }

      // We have to use `getStore().getState()` instead of `useSelector()`, since this
      // function needs to be async, and we need the most up-to-date value:
      const store = getStore();
      const initialIntentId = selectInitialIntentId(store.getState());
      const initialQuery =
        selectInitialQuery(store.getState()) || initialQueryParam;
      // we consider conversation not started if this is initialized from an event
      const isConversationStarted =
        selectIsConversationStarted(store.getState()) && !initialQueryParam;
      const didUserRestartConversation = selectDidUserRestartConversation(
        store.getState(),
      );
      const isTagSelectionHappening = selectIsWorkflowTagSelectionActive(
        store.getState(),
      );
      const isPrivacyConsented = selectIsPrivacyConsented(store.getState());
      const isWidgetEnabled = selectIsWidgetEnabled(store.getState());

      // If widget is disabled, window.Forethought('widget', 'open') should not trigger anything
      if (!isWidgetEnabled) {
        return;
      }

      if (!isPrivacyConsented && !triggerFromPrivacyConsent) {
        dispatch(
          setActionToExecAfterPrivacyConsented('initialIntentTriggered'),
        );
        dispatch(openWidget());
        return;
      }

      if (
        (!!initialIntentId || !!initialQuery) &&
        !isConversationStarted &&
        !isTagSelectionHappening &&
        !didUserRestartConversation
      ) {
        // Other initializations are not done yet, but we have triggered the request already,
        // don't do anything in this case
        if (triggeredRef.current) {
          return;
        }
        triggeredRef.current = true;

        if (initialIntentId) {
          dispatch(setInitialIntentId(initialIntentId));
          dispatch(
            initIntentConversation({
              from_initial_intent_id: true,
              intent: {
                intent_id: initialIntentId,
              },
            }),
          )
            .unwrap()
            .then(() => {
              triggeredRef.current = false;
            });

          dispatch(openWidget());
          if (triggerFromPrivacyConsent) {
            dispatch(privacyConsented());
          }

          if (initialQuery) {
            captureMessage(
              'Both initial-query and initial-intent-id set. Initialized conversation by initial-intent-id and ignored initial-query',
              {
                extra: {
                  initialIntentId,
                  initialQuery,
                  isConversationStarted: isConversationStarted,
                  isTagSelectionHappening: isTagSelectionHappening,
                  origin: ApiService.headers['solve-origin'],
                },
                level: 'info',
              },
            );
          }

          return;
        }

        if (initialQuery) {
          dispatch(setInitialQuery(initialQuery));
          dispatch(
            initIntentConversation({
              intent: { free_form_query: initialQuery },
            }),
          );

          dispatch(openWidget());
          triggeredRef.current = false;
          if (triggerFromPrivacyConsent) {
            dispatch(privacyConsented());
          }
        }
      } else {
        // If has initial-intent-id or initial-query but doesn't pass the condition, use default behavior
        dispatch(openWidget());
        if (triggerFromPrivacyConsent) {
          dispatch(privacyConsented());
        }
      }
    },
    [dispatch, triggerFromPrivacyConsent],
  );

  return onClick;
};

export const useHandleOnWidgetLoad = () => {
  const dispatch = useAppDispatch();
  const kustomerConversationId = useAppSelector(selectChatId);

  const handleKustomerOneChatOnLoad = useCallback(
    (config: OnLoadConfig) => {
      if (
        !kustomerConversationId &&
        config.kustomer_one_chat &&
        config.kustomer_one_chat.enabled
      ) {
        initializeKustomerSdkOnWidgetLoad({
          config: config.kustomer_one_chat,
          onLoginFailure: (err: unknown) => {
            dispatch(
              sendTrackingEvent({
                error: `handleKustomerOneChatOnLoad -- ${err}`,
                event_type: 'widget-handoff-auth-failure',
                integration: 'kustomer-live-chat',
              }),
            );
          },
        });
      }
    },
    [dispatch, kustomerConversationId],
  );

  const handleWidgetOnLoad = useCallback(
    (e: ForethoughtEvent) => {
      if (!isEventTrusted(e)) {
        return;
      }

      if (e.data.event === 'forethoughtWidgetLoaded') {
        handleKustomerOneChatOnLoad(e.data.onLoadConfig);
      }
    },
    [handleKustomerOneChatOnLoad],
  );

  useEffect(() => {
    window.addEventListener('message', handleWidgetOnLoad);

    return () => {
      window.removeEventListener('message', handleWidgetOnLoad);
    };
  }, [handleWidgetOnLoad]);
};

const useGetForethoughtEventTriggerToAction = () => {
  const handleOpenWidget = useHandleOpenWidget();
  const handleShowProactivePrompt = useHandleShowProactivePrompt();
  const handleTriggerIntent = useHandleTriggerIntent();

  return useMemo(
    () => ({
      'open-widget': handleOpenWidget,
      'show-proactive-prompt': handleShowProactivePrompt,
      'trigger-intent': handleTriggerIntent,
    }),
    [handleOpenWidget, handleShowProactivePrompt, handleTriggerIntent],
  );
};

const useHandleOpenWidget = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (proactiveTrigger: ProactiveTrigger) => {
      dispatch(openWidget());
      dispatch(
        sendTrackingEvent({
          event_type: 'proactive-trigger',
          proactive_trigger_id: proactiveTrigger.proactive_trigger_id,
          trigger_type: 'open-widget',
        }),
      );
    },
    [dispatch],
  );
};

const useHandleShowProactivePrompt = () => {
  const dispatch = useAppDispatch();
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);

  return useCallback(
    (proactiveTrigger: ProactiveTrigger) => {
      if (isProactiveChatVisible) return;

      postMessage({ event: 'forethoughtWidgetResizeToMaximumValues' });
      dispatch(showProactivePrompt());
      dispatch(
        sendTrackingEvent({
          event_type: 'proactive-trigger',
          proactive_trigger_id: proactiveTrigger.proactive_trigger_id,
          trigger_type: 'show-proactive-prompt',
        }),
      );
    },
    [dispatch, isProactiveChatVisible],
  );
};

const useHandleTriggerIntent = () => {
  const dispatch = useAppDispatch();
  const handleOpenWidget = useHandleOpenWidget();
  const isConversationStarted = useAppSelector(selectIsConversationStarted);
  const currentInitialIntentId = useAppSelector(selectInitialIntentId);
  const handleWorkflowTriggerOnClick = useHandleWorkflowTriggerOnClick();

  return useCallback(
    (proactiveTrigger: ProactiveTrigger) => {
      const { events_to_trigger, proactive_trigger_id } = proactiveTrigger;
      const params = events_to_trigger?.[0]?.params ?? {};
      const initialIntentIdParam = params?.intent_id;

      if (!initialIntentIdParam || typeof initialIntentIdParam !== 'string') {
        console.error('Cannot trigger intent: missing or invalid Intent ID.');
        return;
      }

      dispatch(setInitialIntentId(initialIntentIdParam));

      if (!isConversationStarted && !currentInitialIntentId)
        dispatch(setLastProactiveTriggerId(proactive_trigger_id));

      handleWorkflowTriggerOnClick();

      if (!isConversationStarted) {
        handleOpenWidget(proactiveTrigger);
      }
    },
    [
      dispatch,
      handleOpenWidget,
      handleWorkflowTriggerOnClick,
      isConversationStarted,
      currentInitialIntentId,
    ],
  );
};

export const useEmitConversationIdGeneratedEvent = () => {
  const isWidgetEnabled = useAppSelector(selectIsWidgetEnabled);
  const conversationId = useAppSelector(selectConversationId);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isUpdateConversationContextViaApiEnabled = featureFlags.includes(
    'update_conversation_context_via_api',
  );

  useEffect(() => {
    if (
      isWidgetEnabled &&
      conversationId &&
      isUpdateConversationContextViaApiEnabled
    ) {
      const conversationIdEvent: ForethoughtWidgetConversationIdGeneratedEvent['data'] =
        {
          conversationId,
          event: 'forethoughtWidgetConversationIdGeneratedEvent',
        };
      postMessage(conversationIdEvent);
    }
  }, [
    isWidgetEnabled,
    conversationId,
    isUpdateConversationContextViaApiEnabled,
  ]);
};

// proactive triggers
export const useHandleProactiveTriggers = () => {
  const proactiveTriggers = useAppSelector(selectProactiveTriggers);

  const forethoughtEventTriggerToAction =
    useGetForethoughtEventTriggerToAction();

  return useCallback(
    (eventName: string) => {
      const containsProactiveTriggers =
        Array.isArray(proactiveTriggers) && proactiveTriggers.length > 0;

      if (!containsProactiveTriggers) {
        return;
      }

      const matchedEvent = proactiveTriggers.find(
        proactiveTrigger => proactiveTrigger.name === eventName,
      );

      if (!matchedEvent) {
        return;
      }

      for (const event of matchedEvent.events_to_trigger) {
        const action = forethoughtEventTriggerToAction[event.type];

        if (!action) continue;

        action(matchedEvent);
      }
    },
    [proactiveTriggers, forethoughtEventTriggerToAction],
  );
};
